import { Button, Input, Label, Spinner, Textarea } from "@qn/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, MessageSquare, PanelLeft, Paperclip, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { NamedIcon } from "../components/NamedIcon";
import type { ProjectInfo } from "../lib/agents";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";
import { uploadFile } from "../lib/upload";

interface Detail {
  project: ProjectInfo;
  files: Array<{ id: string; name: string; size: number }>;
  conversations: Array<{ id: string; title: string; updatedAt: number }>;
}

export function ProjectPage() {
  const t = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { sidebarOpen, set } = usePrefs();
  const q = useQuery({ queryKey: ["project", id], queryFn: () => api<Detail>(`/projects/${id}`) });
  const [name, setName] = useState("");
  const [instr, setInstr] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (q.data) {
      setName(q.data.project.name);
      setInstr(q.data.project.instructions);
    }
  }, [q.data]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["project", id] });
    void qc.invalidateQueries({ queryKey: ["projects"] });
  };
  const save = useMutation({
    mutationFn: () =>
      api(`/projects/${id}`, {
        method: "PUT",
        body: { name, instructions: instr, icon: q.data?.project.icon },
      }),
    onSuccess: refresh,
  });
  const del = useMutation({
    mutationFn: () => api(`/projects/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      refresh();
      navigate("/");
    },
  });
  const addFiles = async (list: FileList) => {
    setErr(null);
    for (const f of Array.from(list)) {
      try {
        const up = await uploadFile(f);
        await api(`/projects/${id}/files`, { method: "POST", body: { fileId: up.id } });
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    }
    refresh();
  };
  const rm = useMutation({
    mutationFn: (fid: string) => api(`/projects/${id}/files/${fid}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  if (q.isLoading || !q.data)
    return (
      <div className="grid flex-1 place-items-center">
        <Spinner />
      </div>
    );
  const p = q.data.project;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--line)] px-3">
        <button
          type="button"
          onClick={() => set({ sidebarOpen: !sidebarOpen })}
          aria-label={t("sidebar.toggle")}
          className={`grid size-9 place-items-center rounded-lg hover:bg-[var(--surface-3)] ${sidebarOpen ? "lg:hidden" : ""}`}
        >
          <PanelLeft className="size-5 rtl:-scale-x-100" />
        </button>
        <NamedIcon name={p.icon} fallback="folder" className="size-10" />
        <h1 className="font-display min-w-0 flex-1 truncate text-lg font-semibold" dir="auto">
          {p.name}
        </h1>
        <Button variant="primary" size="sm" onClick={() => navigate(`/?project=${p.id}`)}>
          <MessageSquare className="size-4" /> {t("projects.newChat")}
        </Button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto grid max-w-5xl gap-6 p-4 sm:p-8 lg:grid-cols-[1fr_20rem]">
          <div className="space-y-5">
            <div>
              <Label htmlFor="pn">{t("models.name")}</Label>
              <Input
                id="pn"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
              />
            </div>
            <div>
              <Label htmlFor="pi" hint={t("projects.instructionsHint")}>
                {t("agents.instructions")}
              </Label>
              <Textarea
                id="pi"
                dir="auto"
                rows={8}
                maxLength={8000}
                value={instr}
                onChange={(e) => setInstr(e.target.value)}
              />
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => del.mutate()}>
                <Trash2 className="size-4 text-[var(--danger)]" /> {t("action.delete")}
              </Button>
              <Button
                variant="primary"
                loading={save.isPending}
                disabled={!name.trim() || (name === p.name && instr === p.instructions)}
                onClick={() => save.mutate()}
              >
                {t("action.save")}
              </Button>
            </div>
            <section>
              <h2 className="font-display mb-2 text-base font-semibold">{t("projects.chats")}</h2>
              {q.data.conversations.length === 0 ? (
                <p className="text-sm text-[var(--fg-faint)]">{t("projects.noChats")}</p>
              ) : (
                <ul className="divide-y divide-[var(--line)] rounded-xl border border-[var(--line)] bg-[var(--surface)]">
                  {q.data.conversations.map((c) => (
                    <li key={c.id}>
                      <Link
                        to={`/c/${c.id}`}
                        className="block truncate px-3.5 py-2.5 text-sm hover:bg-[var(--surface-2)]"
                        dir="auto"
                      >
                        {c.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
          <aside>
            <h2 className="font-display mb-1 text-base font-semibold">{t("projects.knowledge")}</h2>
            <p className="mb-3 text-xs text-[var(--fg-muted)]">{t("projects.knowledgeHint")}</p>
            <input
              ref={input}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) void addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <Button size="sm" variant="outline" onClick={() => input.current?.click()}>
              <Paperclip className="size-4" /> {t("projects.addFiles")}
            </Button>
            {err && (
              <p role="alert" className="mt-2 text-xs text-[var(--danger)]">
                {err}
              </p>
            )}
            <ul className="mt-3 space-y-1.5">
              {q.data.files.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center gap-2 rounded-lg border border-[var(--line)] bg-[var(--surface)] px-2.5 py-2 text-sm"
                >
                  <FileText className="size-4 shrink-0 text-[var(--fg-muted)]" />
                  <span className="min-w-0 flex-1 truncate" dir="auto">
                    {f.name}
                  </span>
                  <button
                    type="button"
                    aria-label={t("action.delete")}
                    onClick={() => rm.mutate(f.id)}
                    className="grid size-6 place-items-center rounded hover:bg-[var(--surface-3)]"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </div>
  );
}
